import { msg } from "@lingui/core/macro";
import { createSignal } from "solid-js";
import { ScanBarcode } from "@fleet/icons";
import { findProductByCode, isScannableCode, type ProductLike } from "@stockpilot/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

interface BarcodeScanInputProps<T extends ProductLike> {
  products: T[];
  onScan: (product: T) => void;
  /** Message when the code does not match any product. */
  onMiss?: (code: string) => void;
}

/**
 * Barcode scan input (Stockpilot F2).
 *
 * Works with an HID reader (keyboard: types the code + Enter) or manual entry.
 * 100% offline: resolves against the already synchronized catalog (pure
 * `findProductByCode` logic). When it finds a product, it calls onScan and clears.
 */
export function BarcodeScanInput<T extends ProductLike>(props: BarcodeScanInputProps<T>) {
  const { t } = useI18n();
  const [code, setCode] = createSignal("");
  const [notFound, setNotFound] = createSignal(false);

  function submit(e: Event) {
    e.preventDefault();
    const raw = code();
    if (!isScannableCode(raw)) {
      return;
    }
    const found = findProductByCode(props.products, raw);
    if (found) {
      setNotFound(false);
      props.onScan(found);
      setCode("");
    } else {
      setNotFound(true);
      props.onMiss?.(raw);
    }
  }

  return (
    <form class="flex flex-col gap-1" onSubmit={submit} data-testid="barcode-form">
      <label class="text-xs text-muted-foreground">{t(msg`Scan barcode or SKU`)}</label>
      <div class="flex items-center gap-2">
        <ScanBarcode class="h-5 w-5 text-muted-foreground shrink-0" />
        <input
          class="w-56 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
          placeholder={t(msg`Barcode / SKU`)}
          value={code()}
          autocomplete="off"
          onInput={(e) => {
            setCode(e.currentTarget.value);
            setNotFound(false);
          }}
          data-testid="barcode-input"
        />
        <Button type="submit" variant="ghost" size="sm" data-testid="barcode-submit-button">
          {t(msg`Find`)}
        </Button>
      </div>
      {notFound() && (
        <p class="text-xs text-danger" data-testid="barcode-not-found">
          {t(msg`No product matches that code.`)}
        </p>
      )}
    </form>
  );
}
